import { memo, useMemo, useState, useEffect } from "react";
import { AttendanceTimeline } from "./AttendanceTimeline";
import {
  buildTimelineSegments,
  buildPendingOverlaySegments,
} from "@/utils/buildTimelineSegments";
import type {
  IAttendanceActivity,
  IPendingAttendanceException,
} from "@/types/(my-space)/timeAttendace.interface";
import type { IShiftInfo } from "@/types/timeAttendance";
import { serverNow } from "@/utils/serverClock";
import { useTranslation } from "react-i18next";

const NOW_INTERVAL_MS = 10_000;

const DEFAULT_SHIFT: IShiftInfo = {
  shiftName: "Default",
  shiftStartTime: "09:00:00",
  shiftEndTime: "18:00:00",
};

interface AttendanceProgressBarProps {
  date: string;
  activities: IAttendanceActivity[];
  shift?: IShiftInfo;
  compact?: boolean;
  barOnly?: boolean;
  isWeekend?: boolean;
  isAbsent?: boolean;
  className?: string;
  /**
   * Expand the rendered window past the shift to cover any out-of-shift
   * activity (evening/late sessions, an open live session). Default false keeps
   * the shift-bounded view used elsewhere.
   */
  fullDay?: boolean;
  /** Overlaid as amber stripes on top of the base timeline — a clock attempt
   * that hasn't been confirmed into a real attendance record yet. */
  pendingExceptions?: IPendingAttendanceException[];
}

export const AttendanceProgressBar = memo(
  ({
    date,
    activities,
    shift,
    compact,
    barOnly,
    isWeekend,
    isAbsent,
    className,
    fullDay = false,
    pendingExceptions = [],
  }: AttendanceProgressBarProps) => {
    const [currentTimeMs, setCurrentTimeMs] = useState(() => serverNow());

    useEffect(() => {
      const id = setInterval(
        () => setCurrentTimeMs(serverNow()),
        NOW_INTERVAL_MS,
      );
      return () => clearInterval(id);
    }, []);

    const activeShift = shift ?? DEFAULT_SHIFT;

    // Pending exceptions stay "pending" until HR acts, so the same rows can
    // still be open days after the attempt — but this bar renders ONE day
    // (`date`). An exception attempted on a different calendar day must not
    // feed the window/overlay math here, or a stale attempt from yesterday
    // stretches today's window back a full day and paints it solid.
    const todaysPendingExceptions = useMemo(
      () =>
        pendingExceptions.filter((e) => {
          if (!e.attemptedTime) return false;
          const attemptedMs = new Date(e.attemptedTime).getTime();
          if (Number.isNaN(attemptedMs)) return false;
          const attemptedDate = new Date(attemptedMs);
          const [y, m, d] = date.split("-").map(Number);
          return (
            attemptedDate.getFullYear() === y &&
            attemptedDate.getMonth() === m - 1 &&
            attemptedDate.getDate() === d
          );
        }),
      [pendingExceptions, date],
    );

    // A pending exception attempted outside the shift window (before it
    // starts, or — most commonly — after "now" has run past shift end) needs
    // the window to stretch and cover it, same as fullDay already does for
    // real activities. Otherwise it's simply off the printed time axis and
    // both the base bar and its overlay render empty.
    const effectiveFullDay = fullDay || todaysPendingExceptions.length > 0;

    const result = useMemo(
      () =>
        buildTimelineSegments(
          date,
          activities,
          activeShift,
          currentTimeMs,
          effectiveFullDay,
          todaysPendingExceptions,
        ),
      [date, activities, activeShift, currentTimeMs, effectiveFullDay, todaysPendingExceptions],
    );

    // Overlay labels are translated when built, so rebuild on a language switch.
    const { i18n } = useTranslation();
    const pendingOverlaySegments = useMemo(
      () =>
        buildPendingOverlaySegments(
          todaysPendingExceptions,
          result.shiftStartMs,
          result.shiftEndMs,
          currentTimeMs,
        ),
      [todaysPendingExceptions, result.shiftStartMs, result.shiftEndMs, currentTimeMs, i18n.language],
    );

    return (
      <AttendanceTimeline
        result={result}
        compact={compact}
        barOnly={barOnly}
        isWeekend={isWeekend}
        isAbsent={isAbsent}
        className={className}
        pendingOverlaySegments={pendingOverlaySegments}
      />
    );
  },
);
